import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Users, Plus, X, Save, ShieldAlert } from "lucide-react";
import { api, extractErrorMessage } from "@/lib/api";
import GidenScopeAuditModal from "@/components/prim/GidenScopeAuditModal";

/**
 * Admin-only panel to configure which pozisyonlar a "personel" user can pick
 * in the Prim modal's "Giden Kişiler" dropdowns. Available choices are derived
 * from the live personnel list so admins never have to type pozisyon strings.
 */
export default function GidenScopePositionsPanel() {
  const [serverPositions, setServerPositions] = useState([]);
  const [draft, setDraft] = useState([]); // selected names (case-sensitive display)
  const [isDefault, setIsDefault] = useState(true);
  const [personnelPositions, setPersonnelPositions] = useState([]); // all pozisyonlar present on personnel
  const [customInput, setCustomInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, pers] = await Promise.all([
        api.get("/prim/giden-scope-positions"),
        api.get("/personnel"),
      ]);
      const list = (cfg.data?.positions || []).map((s) => String(s));
      setServerPositions(list);
      setDraft(list);
      setIsDefault(!!cfg.data?.is_default);
      const rows = Array.isArray(pers.data) ? pers.data : (pers.data?.rows || []);
      const uniq = Array.from(
        new Set(rows.map((p) => (p.pozisyon || "").trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, "tr"));
      setPersonnelPositions(uniq);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Yüklenemedi"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const draftSet = useMemo(
    () => new Set(draft.map((s) => s.toLocaleLowerCase("tr"))),
    [draft]
  );
  const toggleBuiltin = (pos) => {
    if (draftSet.has(pos.toLocaleLowerCase("tr"))) {
      setDraft(draft.filter((d) => d.toLocaleLowerCase("tr") !== pos.toLocaleLowerCase("tr")));
    } else {
      setDraft([...draft, pos]);
    }
  };
  const addCustom = () => {
    const v = customInput.trim();
    if (!v) return;
    if (draftSet.has(v.toLocaleLowerCase("tr"))) { setCustomInput(""); return; }
    setDraft([...draft, v]);
    setCustomInput("");
  };
  const removeChip = (pos) => {
    setDraft(draft.filter((d) => d.toLocaleLowerCase("tr") !== pos.toLocaleLowerCase("tr")));
  };

  const save = async () => {
    if (!draft.length) {
      if (!window.confirm("Boş liste personel kullanıcılarının giden seçmesini imkansız kılar. Devam?")) return;
    }
    setSaving(true);
    try {
      await api.put("/prim/giden-scope-positions", { positions: draft });
      toast.success("Giden kapsamı kaydedildi");
      load();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Kaydedilemedi"));
    } finally {
      setSaving(false);
    }
  };

  const resetDefault = () => setDraft(["Haciz Birimi", "Call Center"]);

  const dirty = draft.length !== serverPositions.length ||
    draft.some((d, i) => (serverPositions[i] || "").toLocaleLowerCase("tr") !== d.toLocaleLowerCase("tr"));

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="giden-scope-panel">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <h4 className="font-semibold text-sm text-slate-900 inline-flex items-center gap-1.5">
            <Users size={14} className="text-emerald-600" />
            Prim · Giden Kişi Kapsamı
          </h4>
          <p className="text-xs text-slate-600 mt-0.5">
            Personel rolünde kullanıcıların "Giden Kişiler" dropdown'ında seçebileceği pozisyon seti.
            {isDefault && <span className="ml-1 text-amber-700">Varsayılan değer kullanılıyor.</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setAuditOpen(true)}
            data-testid="giden-scope-audit-open-btn"
            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded"
            title="Mevcut kapsam dışındaki giden personel ile oluşturulmuş prim satırlarını listele"
          >
            <ShieldAlert size={13} /> Uyumsuzluk Raporu
          </button>
          <button
            onClick={resetDefault}
            data-testid="giden-scope-reset-btn"
            className="text-xs px-2.5 py-1 border border-slate-300 rounded hover:bg-slate-100 text-slate-600"
          >
            Varsayılana Döndür
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="giden-scope-save-btn"
            className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50"
          >
            <Save size={13} /> {saving ? "Kaydediliyor..." : (dirty ? "Değişiklikleri Kaydet" : "Kayıtlı")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5 text-slate-400 text-sm">Yükleniyor...</div>
      ) : (
        <>
          {/* Currently selected chips */}
          <div className="mb-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Seçili pozisyonlar</div>
            <div className="flex flex-wrap gap-1.5 min-h-[32px]" data-testid="giden-scope-selected-chips">
              {draft.length === 0 && <span className="text-xs text-slate-400">Hiç pozisyon seçili değil</span>}
              {draft.map((pos) => (
                <span key={pos} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                  {pos}
                  <button
                    type="button"
                    onClick={() => removeChip(pos)}
                    className="ml-0.5 text-slate-400 hover:text-rose-600"
                    data-testid={`giden-scope-remove-${pos}`}
                    title="Kaldır"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Multi-select from personnel-derived positions */}
          <div className="mb-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Mevcut personel pozisyonları</div>
            <div className="flex flex-wrap gap-1.5" data-testid="giden-scope-available-chips">
              {personnelPositions.length === 0 && (
                <span className="text-xs text-slate-400">Personel yok veya pozisyon tanımsız</span>
              )}
              {personnelPositions.map((pos) => {
                const active = draftSet.has(pos.toLocaleLowerCase("tr"));
                return (
                  <button
                    key={pos}
                    type="button"
                    onClick={() => toggleBuiltin(pos)}
                    data-testid={`giden-scope-toggle-${pos}`}
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${
                      active
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    {active ? "✓ " : "+ "}{pos}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom add (for pozisyonlar not currently assigned to anyone) */}
          <div className="flex items-center gap-1.5">
            <input
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
              placeholder="Özel pozisyon ekle (örn: Avukat)"
              className="text-xs px-2 py-1 border border-slate-300 rounded"
              data-testid="giden-scope-custom-input"
            />
            <button
              onClick={addCustom}
              data-testid="giden-scope-custom-add"
              className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 bg-slate-700 hover:bg-slate-800 text-white rounded"
            >
              <Plus size={12} /> Ekle
            </button>
          </div>
        </>
      )}
      {auditOpen && <GidenScopeAuditModal onClose={() => setAuditOpen(false)} onFixed={() => load()} />}
    </div>
  );
}
